import { Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import * as Sentry from "@sentry/browser";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAlert } from "react-alert";
import "react-datetime/css/react-datetime.css";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import {
  eventDefType,
  eventSourceForEventType,
  isCustomEventType,
} from "../../../eventTypes";
import useAccountMetadata from "../../../hooks/useAccountMetadata";
import { LOAD_EVENTS } from "../../../reducers/events";
import "../../../theme/custom.css";
import { isNetworkFilter, makeRequest, userIsFPEmployee } from "../../../util";

import { CustomEventDisplay, EventEditor } from "./EventsPage";
import { DeleteEventModal } from "./DeleteEventModal";
import { DestinationSettingsModal } from "./DestinationSettingsModal";

import useArrayOfDestinations from "../../../hooks/useArrayOfDestinations";
import EventDestinations from "../../../EventsPage/EventDestinations";

const useStyles = makeStyles(() => ({
  content: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "white",
  },
}));

// Trim semicolons and whitespace from end of SQL query
function trimSQLQuery(query) {
  return query.replace(/[\s;]+$/, "");
}

export function EventEditorDrawer({ event, onClose }) {
  const classes = useStyles();
  const alert = useAlert();
  const location = useLocation();
  const dispatch = useDispatch();
  const accountMetadata = useAccountMetadata();

  const [enabledDests, setEnabledDests] = useState(null);
  const [disabledBackfills, setDisabledBackfills] = useState(null);
  const [eventEditorState, setEventEditorState] = useState({});
  const [unsavedEdits, setUnsavedEdits] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [deleteEventModalOpen, setDeleteEventModalOpen] = useState(false);
  const [
    destinationSettingsModalOpen,
    setDestinationSettingsModalOpen,
  ] = useState(false);
  const [screenshot, setScreenshot] = useState(null);

  const destinations = useArrayOfDestinations();

  const { eventDefs, loading: eventDefsLoading } = useSelector(
    (state) => state.eventDefs
  );
  const events = useMemo(
    () => _.sortBy(eventDefs, (eventDef) => eventDef.name.toLowerCase()),
    [eventDefs]
  );
  const setEvents = useCallback(
    (data) => dispatch({ type: LOAD_EVENTS, payload: data }),
    [dispatch]
  );

  // KLUDGE: When changing the event definition settings, we want to immediately
  // change the event definition state, then trigger a save. We can't call
  // setState then make a request because the setState wouldn't have propagated
  // yet. Instead, we call setTriggerSaveEvent which will save the event the
  // next time through.
  const [triggerSaveEvent, setTriggerSaveEvent] = useState(false);

  // This can be either 'create' or 'edit' depending on whether we are creating
  // a new event or editing an old one.
  const [editorType, setEditorType] = useState(null);

  const handleNewEventDefinition = useCallback(
    (type = "click", typeSpecificProperty = "", filters = []) => {
      setEnabledDests({});
      setDisabledBackfills({});
      setUnsavedEdits(true);
      setEventEditorState({
        id: uuidv4(),
        name: "",
        typeSpecificFilterVal: typeSpecificProperty,
        type: type,
        filters: filters,
        snapshots: [],
        segmentCompatible: false,
      });
      setEditorType("create");
      setErrorMessage("");
      setScreenshot("");
    },
    []
  );

  // Select the given event from the event list.
  const handleSelectEvent = useCallback((event) => {
    function convertStorageToUIFilters(type, stFilters) {
      if (type === "network") {
        let uiFilters = [];
        for (let stFilter of stFilters) {
          let res = convertStoragePropToUIPropAndElPath(stFilter);
          let uiFilter = {
            type: stFilter.type,
            prop: res[0],
            nw_path: res[1],
            op: stFilter.op,
            val: stFilter.val,
          };
          uiFilters.push(uiFilter);
        }
        return uiFilters;
      } else {
        return stFilters;
      }
    }

    function convertStoragePropToUIPropAndElPath(stFilter) {
      if (isNetworkFilter(stFilter)) {
        return ["$network", stFilter.prop];
      } else {
        return [stFilter.prop, ""];
      }
    }

    const eventType = eventDefType(event);
    var filters = event.filter.filters;

    var typeSpecificFilter;
    if (eventType === "pageview") {
      typeSpecificFilter = filters.find(
        (filter) => filter.prop === "$pathname"
      );
    } else if (eventType === "network") {
      typeSpecificFilter = filters.find(
        (filter) => filter.prop === "$pathname"
      );
    } else if (eventType === "react_navigation_screenview") {
      typeSpecificFilter = filters.find(
        (filter) => filter.prop === "$screen_path"
      );
    } else if (eventType === "batch_event") {
      filters = [];
      typeSpecificFilter = { val: event.sql_query };
    } else {
      typeSpecificFilter = filters.find(
        (filter) => filter.prop === "$elements"
      );
    }

    filters = convertStorageToUIFilters(
      eventType,
      filters.filter(
        (filter) =>
          filter.prop !== "$event_type" && filter !== typeSpecificFilter
      )
    );

    const editorState = {
      ...event,
      typeSpecificFilterVal: typeSpecificFilter && typeSpecificFilter.val,
      type: eventDefType(event),
      filters: filters,
      snapshots: event.snapshots || [],
      segmentCompatible: event.segment_compatible || false,
    };

    setEventEditorState(editorState);
    setUnsavedEdits(false);
    setEnabledDests(event.enabled_dests);
    setDisabledBackfills({});
    setEditorType("edit");
    setErrorMessage("");
    makeRequest("get_event_def_screenshot", { event_def_id: event.id }).then(
      (response) => {
        const screenshot = response.data;
        setScreenshot(screenshot);
      }
    );
  }, []);

  // Returns the type specific filter for the event current in the event def
  // editor.
  function editorStateTypeSpecificFilter() {
    if (eventEditorState.type === "pageview") {
      return {
        type: "prop_filter",
        prop: "$pathname",
        op: "wildcard_matches",
        val: eventEditorState.typeSpecificFilterVal,
      };
    } else if (eventEditorState.type === "network") {
      return {
        type: "prop_filter",
        prop: "$pathname",
        op: "wildcard_matches",
        val: eventEditorState.typeSpecificFilterVal,
      };
    } else if (eventEditorState.type === "react_navigation_screenview") {
      return {
        type: "prop_filter",
        prop: "$screen_path",
        op: "wildcard_matches",
        val: eventEditorState.typeSpecificFilterVal,
      };
    } else {
      return {
        type: "prop_filter",
        prop: "$elements",
        op: "css_matches",
        val: eventEditorState.typeSpecificFilterVal,
      };
    }
  }

  function convertUIPropAndElPathToStorageProp(uiFilter) {
    if (isNetworkFilter(uiFilter)) {
      return uiFilter.nw_path;
    } else {
      return uiFilter.prop;
    }
  }

  function convertUIToStorageFilters(type, uiFilters) {
    if (type === "network") {
      let stFilters = [];
      for (let uiFilter of uiFilters) {
        let stFilter = {
          type: uiFilter.type,
          prop: convertUIPropAndElPathToStorageProp(uiFilter),
          op: uiFilter.op,
          val: uiFilter.val,
        };
        stFilters.push(stFilter);
      }
      return stFilters;
    } else {
      return uiFilters;
    }
  }

  // Takes the current editor state and converts it into an event definition.
  function editorStateToEventDef() {
    var filters;

    if (isCustomEventType(eventEditorState.type)) {
      // For custom events, there isn't an $event_type filter or event type
      // specific filter so we just included the filters as is.
      filters = eventEditorState.filters;
    } else if (eventEditorState.type === "batch_event") {
      filters = [
        {
          type: "prop_filter",
          prop: "$event_def_id",
          op: "=",
          val: eventEditorState.id,
        },
        {
          type: "prop_filter",
          prop: "mp_lib",
          op: "=",
          val: "sql",
        },
      ];
    } else {
      filters = [
        {
          type: "prop_filter",
          prop: "$event_type",
          op: "=",
          val: eventEditorState.type,
        },
        editorStateTypeSpecificFilter(),
      ].concat(
        convertUIToStorageFilters(
          eventEditorState.type,
          eventEditorState.filters
        )
      );
    }

    return {
      // by default, the event should have an empty tags object.
      // eventEditorState.tags will override this if present.
      tags: {},
      ...eventEditorState,
      filter: {
        type: "and",
        filters: filters,
      },
      source: eventSourceForEventType(eventEditorState.type),
      snapshots: eventEditorState.snapshots,
      segment_compatible: eventEditorState.segmentCompatible,
      description: eventEditorState.description,
      sql_query:
        (eventEditorState.type === "batch_event" &&
          trimSQLQuery(eventEditorState.typeSpecificFilterVal)) ||
        null,
    };
  }

  // This updates the events array by replacing the event with the given id with
  // fn() applied to it.
  const updateEventDef = useCallback(
    function updateEventDef(eventDefID, fn) {
      const existingEventDef = events.find(
        (eventDef) => eventDef.id === eventDefID
      );
      const newEventDef = fn(existingEventDef);
      const eventDefs = events.filter((eventDef) => eventDef.id !== eventDefID);

      if (newEventDef) {
        var insertIndex = eventDefs.findIndex(
          (eventDef) =>
            newEventDef.name.toLowerCase() <= eventDef.name.toLowerCase()
        );

        if (insertIndex === -1) {
          insertIndex = eventDefs.length;
        }

        eventDefs.splice(insertIndex, 0, newEventDef);
      }

      setEvents(eventDefs);
    },
    [events, setEvents]
  );

  function cssSelectorIsValid(selector) {
    try {
      document.querySelectorAll(selector);
      return true;
    } catch {
      return false;
    }
  }

  function hasNetworkEventEditPerms() {
    return accountMetadata.config.networkEventDefs;
  }

  function isNetworkDynProp(snapshot) {
    return snapshot.type === "network";
  }

  function hasJsDynPropEditPerms() {
    return userIsFPEmployee() || accountMetadata.config.allowJsDynamicProp;
  }

  function isEventDefValid(eventDef) {
    if (eventDef.name.length === 0) {
      alert.error("The event definition has no name.");
      return false;
    }

    if (eventDef.filter && eventDef.filter.filters) {
      if (
        eventDef.filter.filters.find(
          (subFilter) =>
            subFilter.op === "css_matches" && !cssSelectorIsValid(subFilter.val)
        )
      ) {
        alert.error("The CSS selector you entered is not valid");
        return false;
      }
    }

    if (
      events.find(
        (otherEventDef) =>
          otherEventDef.name === eventDef.name &&
          otherEventDef.id !== eventDef.id
      )
    ) {
      alert.error(
        "Unable to save event definition. The event definition has the same name as another event."
      );
      return false;
    }

    if (eventEditorState.typeSpecificFilterVal === "") {
      if (eventEditorState.type === "pageview") {
        alert.error("The event definition does not have a URL filter.");
      } else if (eventEditorState.type === "react_navigation_screenview") {
        alert.error("The event definition does not have a screen view filter.");
      } else if (eventEditorState.type === "network") {
        alert.error("The event definition does not have a URL filter.");
      } else if (eventEditorState.type === "batch_event") {
        alert.error("The event definition does not have a SQL query.");
      } else {
        alert.error(
          "The event definition does not have a CSS selector filter."
        );
      }
      return false;
    }

    for (let filter of eventEditorState.filters) {
      if (!filter.prop) {
        alert.error("The property for one of the filters is unselected.");
        return false;
      }

      if (!filter.op) {
        alert.error("The operator for one of the filters is unselected.");
        return false;
      }

      if (isNetworkFilter(filter)) {
        if (eventEditorState.type !== "network") {
          alert.error(
            "There are one or more network-specific filters on a non-Network event definition."
          );
          return false;
        }
        if (!filter.nw_path || !filter.nw_path.startsWith(".")) {
          alert.error(
            "The NW Path for one or more of the network-specific filters does not start with a period."
          );
          return false;
        }
      }
    }

    for (let snapshot of eventEditorState.snapshots) {
      if (!(snapshot.name && snapshot.name !== "")) {
        alert.error(
          "One or more of the dynamic properties does not have a name."
        );
        return false;
      }
      if (!(snapshot.value && snapshot.value !== "")) {
        alert.error(
          "One or more of the dynamic properties does not have a value."
        );
        return false;
      }
      if (isNetworkDynProp(snapshot)) {
        if (eventEditorState.type !== "network") {
          alert.error(
            "There are one or more network-specific dynamic properties on a non-Network event definition."
          );
          return false;
        }
        if (!snapshot.value || !snapshot.value.startsWith(".")) {
          alert.error(
            "One or more network-specific dynamic properties does not have a value starting with a period."
          );
          return false;
        }
      }
    }

    return true;
  }

  // This saves the current event in the event def editor to the backend and
  // updates the local state.

  // handleSaveEvent is a dependency of a useEffect later on. This causes the
  // useEffect to rerun on every refresh. This is ok based on how the useEffect
  // works.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  function handleSaveEvent() {
    const newEventDef = editorStateToEventDef();
    if (!isEventDefValid(newEventDef)) {
      return;
    }

    makeRequest("save_event_def", newEventDef)
      .then((resp) => {
        if (resp.data.error_message) {
          setErrorMessage(resp.data.error_message);
        } else {
          setErrorMessage("");
          const newEventEntry = _.extend({}, newEventDef, {
            updated_at: new Date().toISOString(),
            enabled_dests: enabledDests,
          });
          updateEventDef(newEventEntry.id, () => newEventEntry);

          setUnsavedEdits(false);
          setEditorType("edit");
          alert.success("Event Saved");

          onClose();
        }
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to save event.");
        console.log(error);
        Sentry.captureException(error);
      });
  }

  const handleToggleDestination = useCallback(
    function handleToggleDestination(destID) {
      if (unsavedEdits) {
        alert.error("You have unsaved event definition changes.");
        return;
      }

      const newState = !enabledDests[destID];
      const newDestObj = newState
        ? _.extend(enabledDests, { [destID]: true })
        : _.omit(enabledDests, destID);

      makeRequest("toggle_destination", {
        dest_id: destID,
        event_def_id: eventEditorState.id,
        state: newState,
      })
        .then((resp) => {
          if (resp.data.error_message) {
            setErrorMessage(resp.data.error_message);
          } else {
            setErrorMessage("");
            // const newEnabledDests = _.extend({}, enabledDests, newDestObj);
            setEnabledDests(newDestObj);
            updateEventDef(eventEditorState.id, (eventDef) => {
              return _.extend({}, eventDef, { enabled_dests: newDestObj });
            });
          }
        })
        .catch((error) => {
          alert.error("Something went wrong. Unable to toggle destination.");
          console.log(error);
          Sentry.captureException(error);
        });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [unsavedEdits, enabledDests, eventEditorState.id, alert, updateEventDef]
  );

  const sendBackfillRequest = useCallback(
    function sendBackfillRequest(destID, dateRange) {
      if (unsavedEdits) {
        alert.error("You have unsaved event definition changes.");
        return;
      }

      const defaultStart = new Date();
      defaultStart.setFullYear(defaultStart.getFullYear() - 5);

      const defaultEnd = new Date();
      defaultEnd.setHours(defaultEnd.getHours() + 24);

      const dateRangeWithDefaults = {
        start: dateRange.start || Math.round(defaultStart.getTime() / 1000),
        end: dateRange.end || Math.round(defaultEnd.getTime() / 1000),
      };

      setDisabledBackfills(_.extend({}, disabledBackfills, { [destID]: true }));

      makeRequest("backfill_event_def", {
        event_def_id: eventEditorState.id,
        dest_id: destID,
        datetime_bounds: dateRangeWithDefaults,
      })
        .then(() => alert.success("Backfill Enqueued"))
        .catch((error) => {
          alert.error("Something went wrong. Unable to enqueue backfill.");
          console.log(error);
          setDisabledBackfills(
            _.extend({}, disabledBackfills, { [destID]: false })
          );
          Sentry.captureException(error);
        });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [alert, eventEditorState.id, unsavedEdits, disabledBackfills]
  );

  const handleEditorStateChange = useCallback(function handleEditorStateChange(
    fn
  ) {
    setUnsavedEdits(true);
    setEventEditorState(fn);
  },
  []);

  const handleCloseDeleteEventModal = useCallback(
    function handleCloseDeleteEventModal() {
      setDeleteEventModalOpen(false);
    },
    []
  );

  const handleOpenDestinationsSettingModal = useCallback(
    function handleOpenDestinationsSettingModal() {
      if (unsavedEdits) {
        alert.error("You have unsaved event definition changes.");
        return;
      }

      setDestinationSettingsModalOpen(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [alert, unsavedEdits]
  );

  const handleCloseDestinationSettingsModal = useCallback(
    function handleCloseDestinationSettingsModal() {
      setDestinationSettingsModalOpen(false);
    },
    []
  );

  const handleSaveDestinationSettingsModal = useCallback(
    function handleSaveDestinationSettingsModal(settings) {
      setEventEditorState((oldState) => {
        return _.extend({}, oldState, settings);
      });

      setTriggerSaveEvent(true);
    },
    []
  );

  // After toggling segment compatibility we immediately save the event
  // definition.
  useEffect(
    function () {
      if (triggerSaveEvent) {
        handleSaveEvent();
        setTriggerSaveEvent(false);
      }
    },
    [triggerSaveEvent, handleSaveEvent]
  );

  // Handle prop changes
  useEffect(() => {
    if (!event) {
      // Creating one
      const queryParams = new URLSearchParams(location.search);
      var filters = [];
      if (queryParams.get("targetText")) {
        filters = [
          {
            type: "prop_filter",
            prop: "$el_nested_text",
            nw_path: "",
            op: "=",
            val: queryParams.get("targetText"),
          },
        ];
      }

      handleNewEventDefinition(
        queryParams.get("type") || undefined,
        queryParams.get("typeSpecificProperty") || undefined,
        filters
      );
    } else {
      // Editing one
      handleSelectEvent(event);
    }
  }, [
    event,
    handleNewEventDefinition,
    handleSelectEvent,
    location.search,
    location,
  ]);

  const setEventHidden = useCallback(
    function setEventHidden(isHidden) {
      makeRequest("set_event_def_hidden", {
        event_def_id: eventEditorState.id,
        is_hidden: isHidden,
      })
        .then(() => {
          updateEventDef(eventEditorState.id, (eventDef) => {
            return _.extend({}, eventDef, { is_hidden: isHidden });
          });
          alert.success(isHidden ? "Event Hidden" : "Event Restored");
        })
        .catch((error) => {
          alert.error(
            `Something went wrong. Unable to ${
              isHidden ? "hide" : "restore"
            } event.`
          );
          console.log(error);
          Sentry.captureException(error);
        });
    },
    [alert, eventEditorState.id, updateEventDef]
  );

  const handleDeleteEvent = useCallback(
    function handleDeleteEvent() {
      makeRequest("delete_event", { event_def_id: eventEditorState.id })
        .then(() => {
          setEditorType(null);
          setEnabledDests(null);
          setDisabledBackfills(null);
          setEventEditorState({});
          setUnsavedEdits(false);
          updateEventDef(eventEditorState.id, () => null);
          alert.success("Event Deleted");

          onClose();
        })
        .catch((error) => {
          alert.error("Something went wrong. Unable to delete event.");
          console.log(error);
          Sentry.captureException(error);
        });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [alert, eventEditorState.id, onClose, updateEventDef]
  );

  const isCustomEvent = isCustomEventType(eventEditorState.type);
  return (
    <>
      {!eventDefsLoading && (
        <>
          <div className={classes.content}>
            <Grid container style={{ display: "block" }}>
              {destinations && editorType && (
                <>
                  <Grid item xs={12}>
                    <div>
                      {isCustomEvent ? (
                        <CustomEventDisplay
                          editorState={eventEditorState}
                          setEventHidden={setEventHidden}
                          setEditorState={handleEditorStateChange}
                          onSave={handleSaveEvent}
                        />
                      ) : (
                        <EventEditor
                          key={eventEditorState.id}
                          editorState={eventEditorState}
                          editorType={editorType}
                          errorMessage={errorMessage}
                          setErrorMessage={setErrorMessage}
                          unsavedEdits={unsavedEdits}
                          setState={handleEditorStateChange}
                          onSave={handleSaveEvent}
                          onDelete={() => setDeleteEventModalOpen(true)}
                          screenshot={screenshot}
                          setScreenshot={setScreenshot}
                          hasNetworkEventEditPerms={hasNetworkEventEditPerms}
                          isNetworkDynProp={isNetworkDynProp}
                          hasJsDynPropEditPerms={hasJsDynPropEditPerms}
                          destinations={destinations}
                        />
                      )}
                    </div>
                  </Grid>
                  <Grid item xs={12}>
                    <div>
                      <EventDestinations
                        eventDestinationId={eventEditorState.id}
                        eventType={eventEditorState.type}
                        eventIsCustom={isCustomEvent}
                        destinations={destinations}
                        enabledDests={enabledDests}
                        disabledBackfills={disabledBackfills}
                        handleBackfill={sendBackfillRequest}
                        onToggleDestination={handleToggleDestination}
                        openDestinationsSettingModal={
                          handleOpenDestinationsSettingModal
                        }
                        segmentCompatible={
                          eventEditorState && eventEditorState.segmentCompatible
                        }
                      />
                    </div>
                  </Grid>
                </>
              )}
            </Grid>
          </div>
        </>
      )}
      <DeleteEventModal
        open={deleteEventModalOpen}
        onClose={handleCloseDeleteEventModal}
        onConfirm={handleDeleteEvent}
      />
      <DestinationSettingsModal
        open={destinationSettingsModalOpen}
        onClose={handleCloseDestinationSettingsModal}
        onSave={handleSaveDestinationSettingsModal}
        settings={{
          segmentCompatible:
            eventEditorState && eventEditorState.segmentCompatible,
        }}
      />
    </>
  );
}
